// FeedbackContext.js
import {
  Alert,
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { createContext, useContext, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";

// Icon-based FeedbackButton component
import BugReportIcon from "@mui/icons-material/BugReport";
import FeedbackIcon from "@mui/icons-material/Feedback";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { IconButton, Tooltip } from "@mui/material";
import DialogAtom from "assets/atom/DialogAtom";
import ButtonAtom from "components/ButtonAtom";
import ErrorFallback from "components/ErrorFallback";
import FeedbackImagePicker, {
  takeImages,
} from "components/FeedbackImagePicker";
import { flattenError } from "features/GroundTruth/groundTruthApi";
import { useSnackbar } from "notistack";
import { CSRF_COOKIE_NAME, SARFINDER_BE_API_URL } from "utils/constants";
import { useCallAPI } from "utils/helper";

// Feedback goes up as JSON, or as multipart when images are attached (the
// files under "images", objects such as browser_info as JSON strings).
const postFeedback = async ({ images = [], ...fields }) => {
  const headers = { "X-CSRFToken": Cookies.get(CSRF_COOKIE_NAME) };
  let body;
  if (images.length) {
    body = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      body.append(
        key,
        typeof value === "object" ? JSON.stringify(value) : value,
      );
    });
    images.forEach((file) => body.append("images", file, file.name));
  } else {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(fields);
  }
  const response = await fetch(`${SARFINDER_BE_API_URL}/feedback/`, {
    method: "POST",
    credentials: "include",
    headers,
    body,
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      response.status === 413
        ? "Those images are too large for the server to take."
        : flattenError(payload, response.status),
    );
  }
  return payload;
};

// Create the context
const FeedbackContext = createContext();

// Custom hook to use the feedback context
export const useFeedback = () => {
  const context = useContext(FeedbackContext);
  if (!context) {
    throw new Error("useFeedback must be used within a FeedbackProvider");
  }
  return context;
};

// The main provider component
export const FeedbackProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [details, setDetails] = useState("");
  const [userAction, setUserAction] = useState(""); // New field for what user was doing
  const [errorTrace, setErrorTrace] = useState(""); // New field for error trace
  const [feedbackType, setFeedbackType] = useState("bug");
  const [images, setImages] = useState([]); // {id, file, url} to attach
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [feedbackSubmissions, setFeedbackSubmissions] = useState([]);
  const { enqueueSnackbar } = useSnackbar();

  // API integration hooks
  const submitFeedbackAPI = useMutation({
    mutationKey: ["submitFeedback"],
    mutationFn: postFeedback,
  });

  const fetchFeedbackSubmissions = useCallAPI(
    `fetchFeedbackSubmissions`,
    `/feedback/`,
    "GET",
  );

  // Function to load user's feedback submissions
  const loadUserFeedback = () => {
    fetchFeedbackSubmissions.mutate(null, {
      onSuccess: (data) => {
        setFeedbackSubmissions(data);
      },
      onError: (error) => {
        enqueueSnackbar("Failed to fetch your feedback submissions", {
          variant: "error",
        });
      },
    });
  };

  // Function to open the feedback dialog
  const openFeedback = (options = {}) => {
    // First set the dialog to open before setting values
    setOpen(true);

    // Set form values after a small delay to ensure the dialog is rendered
    setTimeout(() => {
      if (options.autoFilled) {
        setTitle(options.autoFilled.title || "");
        setDetails(options.autoFilled.details || "");
        setUserAction(options.autoFilled.userAction || "");
        setErrorTrace(options.autoFilled.errorTrace || "");
        setFeedbackType(options.autoFilled.type || "bug");
      }
    }, 50);
  };

  const addImages = (files) => {
    const { added, refused } = takeImages(images, files);
    if (added.length) setImages((current) => [...current, ...added]);
    if (refused.length) {
      enqueueSnackbar(refused.join(" "), { variant: "warning" });
    }
  };

  const removeImage = (id) => {
    setImages((current) =>
      current.filter((image) => {
        if (image.id === id) URL.revokeObjectURL(image.url);
        return image.id !== id;
      }),
    );
  };

  // A screenshot pasted anywhere in the dialog is attached; pasted text is
  // left to the field it lands in.
  const handlePaste = (event) => {
    const files = [...(event.clipboardData?.files || [])].filter((file) =>
      file.type.startsWith("image/"),
    );
    if (!files.length) return;
    event.preventDefault();
    addImages(files);
  };

  // Function to close the feedback dialog
  const closeFeedback = () => {
    // First reset all form fields
    setTitle("");
    setDetails("");
    setUserAction("");
    setErrorTrace("");
    setFeedbackType("bug");
    images.forEach((image) => URL.revokeObjectURL(image.url));
    setImages([]);

    // Close the dialog in the next event cycle
    setTimeout(() => {
      setOpen(false);
    }, 0);
  };

  // Function to collect browser information
  const collectBrowserInfo = () => {
    const browserInfo = {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      cookiesEnabled: navigator.cookieEnabled,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      url: window.location.href,
      pathname: window.location.pathname,
    };
    return browserInfo;
  };

  // Shared function to handle feedback submission from both dialog and error boundary
  const handleSubmitFeedback = async (feedbackData) => {
    // If feedbackData wasn't passed, build it from the dialog form
    if (!feedbackData) {
      feedbackData = {
        title,
        feedback_type: feedbackType,
        details,
        user_action: userAction,
        error_trace: errorTrace,
        browser_info: collectBrowserInfo(),
        images: images.map((image) => image.file),
      };
    }

    return new Promise((resolve, reject) => {
      submitFeedbackAPI.mutate(feedbackData, {
        onSuccess: (data) => {
          enqueueSnackbar("Feedback submitted successfully. Thank you!", {
            variant: "success",
          });
          closeFeedback();
          loadUserFeedback(); // Refresh the feedback list
          resolve(data);
        },
        onError: (error) => {
          console.error("Error submitting feedback:", error);
          enqueueSnackbar(
            `Failed to submit feedback. ${error.message || "Please try again."}`,
            { variant: "error" },
          );
          reject(error);
        },
      });
    });
  };

  // Provide the feedback functionality through context
  const value = {
    openFeedback,
    closeFeedback,
    feedbackSubmissions,
    refreshFeedback: loadUserFeedback,
    submitFeedback: handleSubmitFeedback,
  };

  return (
    <FeedbackContext.Provider value={value}>
      {/* Wrap children in ErrorBoundary with the standalone component */}
      <ErrorBoundary
        FallbackComponent={({ error, resetErrorBoundary }) => (
          <ErrorFallback
            error={error}
            resetErrorBoundary={resetErrorBoundary}
            submitFeedback={handleSubmitFeedback}
          />
        )}
      >
        {children}
      </ErrorBoundary>

      {/* Feedback Dialog - only for manual feedback, not error reports */}
      <DialogAtom
        open={open}
        onClose={closeFeedback}
        maxWidth="md"
        fullWidth
        keepMounted={false} // Don't keep dialog mounted when closed
        disableEscapeKeyDown={false} // Allow ESC key to close the dialog
        disableRestoreFocus={false} // Restore focus when dialog closes
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <FeedbackIcon color="primary" />
            <Typography variant="h6">Send Feedback</Typography>
          </Stack>
        </DialogTitle>

        <Divider />

        {/* Alpha stage notice */}
        <Paper
          elevation={0}
          sx={{
            mx: 3,
            mt: 2,
            p: 2,
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <WarningAmberIcon color="primary" />
          <Typography variant="body2">
            This site is still in its{" "}
            <Chip
              size="small"
              label="BETA"
              color="primary"
              component="span" // Fix for Chip inside Typography
            />{" "}
            stage. Your feedback and suggestions are extremely valuable to us
            and will help shape the future of this platform. Thank you for your
            support!
          </Typography>
        </Paper>

        <DialogContent onPaste={handlePaste}>
          <FormControl component="fieldset" sx={{ mb: 2, mt: 1 }}>
            <FormLabel component="legend">Feedback Type</FormLabel>
            <RadioGroup
              row
              value={feedbackType}
              onChange={(e) => setFeedbackType(e.target.value)}
            >
              <FormControlLabel
                value="bug"
                control={<Radio />}
                label="Bug Report"
              />
              <FormControlLabel
                value="feature"
                control={<Radio />}
                label="Feature Request"
              />
              <FormControlLabel
                value="improvement"
                control={<Radio />}
                label="Improvement"
              />
              <FormControlLabel
                value="other"
                control={<Radio />}
                label="Other"
              />
            </RadioGroup>
          </FormControl>

          <TextField
            autoFocus
            margin="dense"
            id="feedback-title"
            label="Title"
            type="text"
            fullWidth
            variant="outlined"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            sx={{ mb: 2 }}
          />

          {feedbackType === "bug" && (
            <TextField
              margin="dense"
              id="user-action"
              label="What were you doing when the issue occurred?"
              multiline
              rows={2}
              fullWidth
              variant="outlined"
              value={userAction}
              onChange={(e) => setUserAction(e.target.value)}
              placeholder="Please describe the steps you were taking when you encountered this issue..."
              sx={{ mb: 2 }}
            />
          )}

          <TextField
            margin="dense"
            id="feedback-details"
            label={feedbackType === "bug" ? "Details of the issue" : "Details"}
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            placeholder={
              feedbackType === "bug"
                ? "Please provide any additional details about the issue..."
                : "Please describe your suggestion or feedback in detail..."
            }
            sx={{ mb: 2 }}
          />

          <FeedbackImagePicker
            images={images}
            onAdd={addImages}
            onRemove={removeImage}
            disabled={submitFeedbackAPI.isPending}
          />

          {/* Error trace field - only shown if there's an error trace or it's a bug report */}
          {(errorTrace || feedbackType === "bug") && (
            <>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 1, mt: 1 }}
              >
                <BugReportIcon fontSize="small" color="error" />
                <Typography variant="body2" color="text.secondary">
                  Error Information (automatically captured)
                </Typography>
              </Stack>
              <TextField
                margin="dense"
                id="error-trace"
                multiline
                rows={4}
                fullWidth
                variant="outlined"
                value={errorTrace}
                onChange={(e) => setErrorTrace(e.target.value)}
                placeholder="Error trace will appear here if an error occurred"
                InputProps={{
                  sx: {
                    fontFamily: "monospace",
                    fontSize: "0.8rem",
                    bgcolor: "rgba(0, 0, 0, 0.03)",
                  },
                  readOnly: false, // Ensure the field is editable
                }}
                // Add a key to force re-render when error trace changes
                key={`error-trace-${Date.now()}`}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <ButtonAtom
            onClick={closeFeedback}
            color="primary"
            variant="outlined"
          >
            Cancel
          </ButtonAtom>
          <ButtonAtom
            onClick={() => handleSubmitFeedback()}
            color="primary"
            variant="contained"
            disabled={
              !title.trim() ||
              (!details.trim() && !userAction.trim()) ||
              submitFeedbackAPI.isPending
            }
          >
            {submitFeedbackAPI.isPending ? "Submitting..." : "Submit"}
          </ButtonAtom>
        </DialogActions>
      </DialogAtom>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </FeedbackContext.Provider>
  );
};

export const FeedbackButton = (props) => {
  const { openFeedback } = useFeedback();
  return (
    <Tooltip title="Send Feedback">
      <IconButton
        color="primary"
        onClick={openFeedback}
        size="medium"
        {...props}
      >
        <FeedbackIcon />
      </IconButton>
    </Tooltip>
  );
};

// New component to display user's feedback history
export const FeedbackHistory = () => {
  const { feedbackSubmissions, refreshFeedback } = useFeedback();

  return (
    <Box sx={{ mt: 4 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <FeedbackIcon color="primary" />
        <Typography variant="h6">Your Feedback History</Typography>
        <Button
          size="small"
          startIcon={<RefreshIcon />}
          onClick={refreshFeedback}
        >
          Refresh
        </Button>
      </Stack>

      {feedbackSubmissions.length === 0 ? (
        <Typography color="text.secondary">
          You haven't submitted any feedback yet.
        </Typography>
      ) : (
        <Stack spacing={2}>
          {feedbackSubmissions.map((submission) => (
            <Paper
              key={submission.id}
              sx={{
                p: 2,
                borderLeft: 4,
                borderColor:
                  submission.feedback_type.value === "bug"
                    ? "error.main"
                    : submission.feedback_type.value === "feature"
                      ? "primary.main"
                      : submission.feedback_type.value === "improvement"
                        ? "success.main"
                        : "grey.500",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  {submission.title}
                </Typography>
                <Chip
                  size="small"
                  label={submission.status.display}
                  color={
                    submission.status.value === "submitted"
                      ? "default"
                      : submission.status.value === "in_review"
                        ? "primary"
                        : submission.status.value === "resolved"
                          ? "success"
                          : "default"
                  }
                />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {new Date(submission.date_submitted).toLocaleString()}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {submission.details.length > 150
                  ? `${submission.details.substring(0, 150)}...`
                  : submission.details}
              </Typography>
              {submission.admin_response && (
                <Box
                  sx={{ mt: 2, p: 1, bgcolor: "action.hover", borderRadius: 1 }}
                >
                  <Typography variant="subtitle2">Admin Response:</Typography>
                  <Typography variant="body2">
                    {submission.admin_response}
                  </Typography>
                </Box>
              )}
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );
};
